import React, { useState } from 'react'

import { useLocation, useNavigate, useParams } from 'react-router-dom'

import UIButton from '../../components/UI/UIButton'

import { Icon } from '../../components/Icon'
import { ReactComponent as LogoSvg } from '../../assets/svgs/logos/Logo.svg'
import { setupPassword } from '../../api/auth/password'
import { ErrorResponse, getSinglePolicy } from '../../utils/types'
import { toast } from 'react-toastify'
import UIPasswordInput from '../../components/UI/UIPasswordInput'

const SetPassword = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useParams()
  const searchParams = new URLSearchParams(location.search)
  const uid = searchParams.get('uid')

  const [isLoading, setIsLoading] = useState(false)
  const [password, SetPassword] = useState('')
  const [confirmPassword, SetConfirmPassword] = useState('')

  const createPassword = async (): Promise<void> => {
    setIsLoading(true)
    const minLength = 8
    const hasSpecialSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    const hasUpperCase = /[A-Z]/.test(password)

    // Check conditions
    if (password.length < minLength || !hasSpecialSymbol || !hasUpperCase) {
      // Password doesn't meet requirements, show toast
      toast.error(
        'Password must be at least 8 characters long and contain at least one special symbol and one uppercase letter.',
        {
          position: 'top-right',
        }
      )
      return // Exit function early if validation fails
    }

    try {
      const response = (await setupPassword(
        {
          password: password,
          confirmPassword: confirmPassword,
          token: token as string,
        },
        uid as string
      )) as {
        status?: string
        data: getSinglePolicy
      }
      if (response?.status == 'successful') {
        SetPassword('')
        SetConfirmPassword('')
        toast.success('Password Setup Successfully.', {
          position: 'top-right',
        })
        navigate('/')
        setIsLoading(false)
      } else {
        toast.error('Network Error.', {
          position: 'top-right',
        })
        setIsLoading(false)
      }
    } catch (error) {
      const errors = error as ErrorResponse
      if (errors?.response && errors?.response?.data) {
        const errorMessage =
          errors?.response?.data?.message || 'An error occurred'
        toast.error(errorMessage, {
          position: 'top-right',
        })
      } else {
        toast.error('An unexpected error occurred', {
          position: 'top-right',
        })
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="mx-5 sm:mx-auto sm:w-full sm:max-w-lg flex">
        <div className="flex gap-4 flex-1 ">
          <LogoSvg width={130} height={36} />
        </div>
      </div>
      <div className="mx-5 sm:mx-auto mt-8 sm:w-full sm:max-w-lg">
        <div className="bg-white py-10 px-4 shadow rounded-[1rem] sm:px-10">
          <div onClick={() => navigate(-1)} className="cursor-pointer w-fit">
            <Icon
              name={'IconArrowNarrowLeft'}
              width={24}
              height={24}
              strokeWidth={1.5}
              stroke={'#02698C'}
            />
          </div>
          <div className="flex flex-col py-2 gap-3 justify-center items-center text-center mt-7">
            <Icon
              name="IconPasscodeLock"
              width={52}
              height={52}
              strokeWidth={1}
              stroke={'#02698C'}
            />
            <p className="text-lg font-bold text-text2">Setup your password</p>
            <p className="text-sm font-normal text-text5">
              Please enter your Password
            </p>
          </div>
          <div className="mt-8">
            <div className="flex flex-col gap-5">
              <div>
                <UIPasswordInput
                  label={'Password'}
                  value={password}
                  onChange={(e) => {
                    SetPassword(e.target.value)
                  }}
                  //   onBlur={handleBlur('email')}
                  placeholder="Please enter your Password"
                  iconName={'IconLock'}
                />
              </div>
              <div>
                <UIPasswordInput
                  label={'Confirm Password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    SetConfirmPassword(e.target.value)
                  }}
                  //   onBlur={handleBlur('email')}
                  placeholder="Please enter your Password"
                  iconName={'IconLock'}
                  labelBottomList={[
                    `Length: Make your password at least 8 - 12 characters long.`,
                    `Mix It Up: Use a combination of uppercase and lowercase letters, numbers, and special characters.`,
                    `Avoid Common Patterns: Stay away from easy-to-guess sequences like "123456" or "password"`,
                    `No Personal Info: Don't use personal information like names or birthdays in your password. Keep your password strong and secure to protect your personal data!`,
                  ]}
                />
              </div>
            </div>

            <div className="container mx-auto mt-8">
              <UIButton
                onClick={createPassword}
                label="Submit"
                loading={isLoading}
              />
            </div>
          </div>
        </div>

        <div className="sm:mx-auto w-full h-auto px-8 my-4 py-6 rounded-[1rem] gap-16 bg-gray2">
          <div className="flex gap-3 items-center">
            <Icon
              name={'IconCheckMonogram'}
              width={16}
              height={16}
              strokeWidth={1}
            />
            <div
              className="cursor-pointer"
              onClick={() => navigate('/privacy-policy')}
            >
              <p className="font-medium text-xs text-text1">Privacy Policy</p>
            </div>
            <div
              className="cursor-pointer"
              onClick={() => navigate('/terms-condiditions')}
            >
              <p className="font-medium text-xs text-text1">
                Terms & Conditions
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="font-medium text-xs text-text3">
              © 2025 Streamlearn Ltd. All rights reserved. 360 Safeguarding is
              a trading name of Streamlearn Ltd. Company No. 12180453.
              Registered office: Axis 19 Mallard Way, Riverside Business Park,
              Swansea Vale, Swansea, SA7 0AJ.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SetPassword
